<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'base-upload-button',
})
</script>
<template>
  <el-upload
    class="upload-wrap"
    action=""
    :http-request="handleUpload"
    :data="data"
    :name="name"
    :before-upload="handleBeforeUpload"
    :show-file-list="false"
    :format="format"
    :with-credentials="true"
    multiple
    :accept="accept"
    :disabled="disabled || loading"
  >
    <el-button
      v-blur
      :type="type"
      :link="link"
      :size="size"
      :plain="plain"
      :round="round"
      :circle="circle"
      :disabled="disabled"
      :icon="icon"
      :loading="showLoading && loading"
    >
      {{ label }}
    </el-button>
  </el-upload>
</template>
<script lang="ts" setup name="base-upload-button">
  import { ref, PropType } from 'vue';
  import { ElMessage } from 'element-plus';
  import { uploadByUrlRequest } from '@/api/modules/common';
  import type { EpPropMergeType } from 'element-plus/es/utils/vue/props/types';

  const props = defineProps({
    /**
     * 说明: 上传的地址
     * 类型: string
     * 可选值: -
     * 默认值: -
     */
    action: {
      required: true,
      type: String,
    },
    /**
     * 说明: 可接受的地址
     * 类型: string
     * 可选值: -
     * 默认值: -
     */
    accept: {
      type: String,
      default: '',
    },
    /**
     * 说明: 上传时附带的额外参数
     * 类型: object
     * 可选值: -
     * 默认值: -
     */
    data: {
      type: Object,
      default: () => {},
    },
    /**
     * 说明: 按钮文案描述、上传或者导入等
     * 类型: string
     * 可选值: -
     * 默认值: 导入
     */
    label: {
      type: String,
      default: '导入',
    },
    /**
     * 说明: 上传的文件字段名
     * 类型: string
     * 可选值: -
     * 默认值: file
     */
    name: {
      type: String,
      default: 'file',
    },
    /**
     * 说明: 是否禁用
     * 类型: string
     * 可选值: -
     * 默认值: false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 图标
     * 类型: string
     * 可选值: -
     * 默认值: -
     */
    icon: {
      type: String as any,
      default: '',
    },
    /**
     * 说明: 要检查的文件类型
     * 类型: array
     * 可选值: ['xls', 'xlsx', 'xlc', 'xlm', 'xlt', 'xlw', 'xltx', 'xlsm', 'xlsb', 'pdf', 'png', 'jpg']等
     * 默认值: 不检查文件类型是否合适
     */
    format: {
      type: Array,
      default: () => [],
    },
    /**
     * 说明: 按钮类型
     * 类型: string
     * 可选值: 'primary'| 'success'| 'warning'| 'danger'| 'info'| 'text'(delete)
     * 默认值: primary
     */
    type: {
      type: String as PropType<
        EpPropMergeType<StringConstructor, 'primary'|'success'|'warning'|'danger'|'info', unknown>
      >,
      default: '',
    },
    /**
     * 说明: 上传的时候是否在按钮前边展示loading
     * 类型: boolean
     * 可选值: true / false
     * 默认值: small
     */
    showLoading: {
      type: Boolean,
      default: true,
    },
    /**
     * 说明: 尺寸
     * 类型: string
     * 可选值: large / small / default
     * 默认值: default
     */
    size: {
      type: String as PropType<
        EpPropMergeType<StringConstructor, 'large'|'small'|'default', unknown>
      >,
      default: '',
    },
    /**
     * 说明: 是否为链接按钮，去掉以前的type='text'
     * 类型: boolean
     * 可选值: -
     * 默认值: false
     */
    link: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否朴素按钮
     * 类型: boolean
     * 可选值: -
     * 默认值: false
     */
    plain: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否圆角按钮
     * 类型: boolan
     * 可选值: -
     * 默认值: false
     */
    round: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否圆形按钮
     * 类型: boolan
     * 可选值: -
     * 默认值: false
     */
    circle: {
      type: Boolean,
      default: false,
    },
    /**
     * @description: 上传前的校验
     */
    beforeUpload: {
      type: Function as PropType<(file:TypeObject) => boolean> | undefined,
      default: undefined,
    },
  });
  const emit = defineEmits(['on-error', 'on-success']);
  const loading = ref(false);

  const handleBeforeUpload = (file:TypeObject) => {
    if (props.beforeUpload) {
      return props.beforeUpload(file);
    }

    const fileExtension = file && file.name.substring(file.name.lastIndexOf('.') + 1);
    if (props.format && props.format.length > 0) {
      if (!props.format.includes(fileExtension)) {
        ElMessage({
          message: `请上传以下类型的文件：${props.format.join('、')}`,
          type: 'warning',
          grouping: true,
        });
        return false;
      }
    }
    return true;
  };

  const handleUpload:Func = (params:TypeObject) => {
    if (!props.action) {
      return;
    }
    const formData = new FormData();
    const data = props.data || {};
    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        formData.append(key, data[key]);
      }
    }
    formData.append(props.name, params.file);
    loading.value = true;
    uploadByUrlRequest(props.action, formData).then((res) => {
      console.log(res, '----上传成功');
      emit('on-success', res, params.file);
    }).catch((err) => {
      console.log('上传失败-=====');
      const error = err && err.message ? JSON.parse(err.message) : {};
      emit('on-error', error, params.file);
    }).finally(() => {
      loading.value = false;
    });
  };
</script>
<style scoped lang="scss">
.upload-wrap {
  display: inline;
  align-items: center;
}
</style>
